/**
 * DISCOVERY-INTELLIGENCE-000001 — Engineering lab + business field knowledge UI.
 * No warehouse/BI/AI implementation.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  amazonConnectApi,
  type DiscoveryEndpointPackage,
  type DiscoveryExplorerPayload,
  type DiscoveryIntelligence,
  type DiscoveryRecipe,
  type SellerConnection,
} from '../../api/amazonconnect';

type Tab =
  | 'apis'
  | 'recipes'
  | 'schema'
  | 'reports'
  | 'fields'
  | 'graph'
  | 'capabilities'
  | 'field_catalog'
  | 'lineage'
  | 'warehouse'
  | 'consumers'
  | 'downloads';

function statusClass(s: string): string {
  const u = (s || '').toUpperCase();
  if (u === 'SUCCESS' || u === 'COMPLETED') return 'bg-emerald-100 text-emerald-900';
  if (u.startsWith('BLOCKED_') || u === 'NOT_APPLICABLE') return 'bg-amber-100 text-amber-900';
  if (u === 'ERROR') return 'bg-red-100 text-red-900';
  if (u === 'UNKNOWN' || s === 'success') return 'bg-slate-100 text-slate-700';
  if (s === 'permission_denied' || s === 'skipped' || s === 'cataloged') return 'bg-amber-50 text-amber-800';
  return 'bg-slate-50 text-slate-600';
}

export function AmazonDiscoveryPage() {
  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [data, setData] = useState<DiscoveryExplorerPayload | null>(null);
  const [intel, setIntel] = useState<DiscoveryIntelligence | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState<Tab>('apis');
  const [selectedKey, setSelectedKey] = useState('');
  const [pkg, setPkg] = useState<DiscoveryEndpointPackage | null>(null);
  const [pkgLoading, setPkgLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const conns = await amazonConnectApi.listConnections();
      setConnections(conns);
      const cid = connectionId || conns[0]?.id || '';
      if (!connectionId && conns[0]?.id) setConnectionId(conns[0].id);
      if (!cid) {
        setData(null);
        setIntel(null);
        return;
      }
      const [disc, int] = await Promise.all([
        amazonConnectApi.getDiscovery(cid),
        amazonConnectApi.getDiscoveryIntelligence(cid).catch(() => null),
      ]);
      setData(disc);
      setIntel(int);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load discovery');
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadSchema = useCallback(
    async (key: string, nextTab?: Tab) => {
      if (!connectionId || !key) {
        setPkg(null);
        return;
      }
      setPkgLoading(true);
      setError(null);
      try {
        setPkg(await amazonConnectApi.getDiscoveryEndpoint(connectionId, key));
        setSelectedKey(key);
        setTab(nextTab || 'schema');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Schema load failed');
      } finally {
        setPkgLoading(false);
      }
    },
    [connectionId],
  );

  const run = async () => {
    if (!connectionId) return;
    setRunning(true);
    setError(null);
    try {
      await amazonConnectApi.runDiscovery(connectionId);
      // Lab runs async (nginx 120s); poll until completed or 45 min.
      const deadline = Date.now() + 45 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        await load();
        const st = await amazonConnectApi.discoveryStatus(connectionId).catch(() => null);
        const status = st?.last_run?.status || '';
        if (status && status !== 'running' && status !== 'accepted' && status !== 'queued') {
          break;
        }
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Discovery lab run failed');
    } finally {
      setRunning(false);
    }
  };

  const runAgain = async (key: string) => {
    if (!connectionId) return;
    setRunning(true);
    setError(null);
    try {
      await amazonConnectApi.runDiscoveryEndpoint(connectionId, key);
      const deadline = Date.now() + 10 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        await load();
        const st = await amazonConnectApi.discoveryStatus(connectionId).catch(() => null);
        const status = st?.last_run?.status || '';
        if (status && status !== 'running' && status !== 'accepted' && status !== 'queued') {
          break;
        }
      }
      await loadSchema(
        key,
        tab === 'recipes' ? 'recipes' : tab === 'field_catalog' ? 'field_catalog' : 'schema',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Re-run failed');
    } finally {
      setRunning(false);
    }
  };

  const st = data?.status;
  const qualityByKey = useMemo(() => {
    const m = new Map<string, number>();
    for (const q of data?.quality_scores ?? []) {
      m.set(q.endpoint_key, q.overall);
    }
    return m;
  }, [data?.quality_scores]);

  const recipeByKey = useMemo(() => {
    const m = new Map<string, DiscoveryRecipe>();
    for (const r of data?.recipes ?? []) {
      m.set(r.endpoint_key, r);
    }
    return m;
  }, [data?.recipes]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'apis', label: 'APIs' },
    { id: 'recipes', label: 'Recipes' },
    { id: 'schema', label: 'Schema' },
    { id: 'field_catalog', label: 'Field Catalog' },
    { id: 'capabilities', label: 'Business Capabilities' },
    { id: 'lineage', label: 'Field Lineage' },
    { id: 'warehouse', label: 'Warehouse Candidate' },
    { id: 'consumers', label: 'Consumers' },
    { id: 'downloads', label: 'Downloads' },
    { id: 'reports', label: 'Reports' },
    { id: 'fields', label: 'Field dictionary' },
    { id: 'graph', label: 'Execution graph' },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <header className="space-y-2 border-b border-slate-200 pb-4">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          DISCOVERY-INTELLIGENCE-000001 · Technical discovery → business knowledge · Amazon is SoT
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Amazon SP-API Engineering Lab
        </h1>
        <p className="max-w-3xl text-sm leading-relaxed text-slate-600">
          Field catalogs, capability matrix, lineage, and warehouse blueprint candidates. Docs only —
          no warehouse, BI, or AI execution.
        </p>
        <div className="flex flex-wrap gap-3 text-sm">
          <Link to="/amazon/system-explorer" className="text-brand-700 hover:underline">
            System Explorer
          </Link>
          <Link to="/amazon/connect" className="text-brand-700 hover:underline">
            Amazon Connect
          </Link>
          <a
            href="https://www.postman.com/amazon-selling-partner-api/selling-partner-api/overview"
            target="_blank"
            rel="noreferrer"
            className="text-brand-700 hover:underline"
          >
            Official Postman workspace
          </a>
        </div>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="text-slate-500">Connection</span>
          <select
            className="mt-1 block min-w-[16rem] rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            value={connectionId}
            onChange={(e) => setConnectionId(e.target.value)}
          >
            {connections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.seller_ref || c.id.slice(0, 8)} · {c.status}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => void load()}
          className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm"
        >
          Refresh
        </button>
        <button
          type="button"
          disabled={!connectionId || running}
          onClick={() => void run()}
          className="cursor-pointer rounded-md bg-slate-900 px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {running ? 'Running lab…' : 'Run discovery lab'}
        </button>
        <button
          type="button"
          disabled={!connectionId}
          onClick={() =>
            void amazonConnectApi.downloadDiscoveryAllZip(connectionId).catch((e) => setError(String(e)))
          }
          className="cursor-pointer rounded-md border border-slate-800 px-3 py-1.5 text-sm font-medium text-slate-900"
        >
          Download All (ZIP)
        </button>
        <button
          type="button"
          disabled={!connectionId}
          onClick={() =>
            void amazonConnectApi
              .downloadDiscoveryIntelligenceZip(connectionId)
              .catch((e) => setError(String(e)))
          }
          className="cursor-pointer rounded-md border border-slate-400 px-3 py-1.5 text-sm"
        >
          Intelligence docs (ZIP)
        </button>
        <button
          type="button"
          disabled={!connectionId}
          onClick={() =>
            void amazonConnectApi
              .downloadDiscoveryFieldMatrixCSV(connectionId)
              .catch((e) => setError(String(e)))
          }
          className="cursor-pointer rounded-md border border-emerald-600 px-3 py-1.5 text-sm text-emerald-900"
        >
          Export API Field Matrix (CSV)
        </button>
        <button
          type="button"
          disabled={!connectionId}
          onClick={() =>
            void amazonConnectApi
              .downloadDiscoveryFieldMasterCSV(connectionId)
              .catch((e) => setError(String(e)))
          }
          className="cursor-pointer rounded-md border border-emerald-600 px-3 py-1.5 text-sm text-emerald-900"
        >
          Export Field Master (CSV)
        </button>
      </div>

      {error && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {error}
        </p>
      )}
      {loading && <p className="text-sm text-slate-500">Loading laboratory catalog…</p>}

      {st && (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-900">Lab coverage</h2>
          <p className="mt-1 text-xs text-slate-500">{st.note}</p>
          <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-slate-500">Endpoints</dt>
              <dd className="font-mono">
                {st.endpoints_success} SUCCESS · {st.endpoints_permission_issues} blocked ·{' '}
                {st.endpoints_error} ERROR / {st.endpoints_total}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Unresolved · recipes</dt>
              <dd className="font-mono">
                {data?.unresolved?.length ?? 0} · {data?.recipes?.length ?? 0}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Schema / download / relationships</dt>
              <dd className="font-mono">
                {st.schema_status} · {st.download_status} · {st.relationship_status}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Quality avg · fields · samples</dt>
              <dd className="font-mono">
                {(st.avg_quality_score ?? 0).toFixed?.(1) ?? st.avg_quality_score}% · {st.fields_count} ·{' '}
                {st.samples_count}
              </dd>
            </div>
          </dl>
        </section>
      )}

      <nav className="flex flex-wrap gap-1 border-b border-slate-200" aria-label="Lab sections">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`cursor-pointer px-3 py-2 text-sm ${
              tab === t.id
                ? 'border-b-2 border-slate-900 font-medium text-slate-900'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {data && tab === 'apis' && (
        <section>
          <div className="max-h-[32rem] overflow-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-left text-xs">
              <thead className="sticky top-0 bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-2 py-2">API</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">HTTP</th>
                  <th className="px-2 py-2">Q%</th>
                  <th className="px-2 py-2">Why / how to unblock</th>
                  <th className="px-2 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.endpoints.map((e) => (
                  <tr key={e.endpoint_key} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">
                      <div className="font-mono text-[11px]">{e.endpoint_key}</div>
                      <div className="text-[10px] text-slate-500">
                        {e.http_method || 'GET'} {e.path_template}
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] ${statusClass(e.last_status)}`}>
                        {e.last_status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-mono">{e.http_status || '—'}</td>
                    <td className="px-2 py-1.5 font-mono">
                      {(e.quality_score ?? qualityByKey.get(e.endpoint_key) ?? 0).toFixed?.(0) ?? '—'}
                    </td>
                    <td className="max-w-sm px-2 py-1.5 text-[11px] text-slate-600">
                      <div className="truncate" title={e.error_message}>
                        {e.error_message || '—'}
                      </div>
                      {(e.how_to_unblock || recipeByKey.get(e.endpoint_key)?.how_to_unblock) && (
                        <div
                          className="mt-0.5 truncate text-[10px] text-slate-500"
                          title={e.how_to_unblock || recipeByKey.get(e.endpoint_key)?.how_to_unblock}
                        >
                          Unblock: {e.how_to_unblock || recipeByKey.get(e.endpoint_key)?.how_to_unblock}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          className="cursor-pointer rounded border border-slate-300 px-1.5 py-0.5 text-[10px] hover:bg-slate-50"
                          onClick={() => void loadSchema(e.endpoint_key, 'recipes')}
                        >
                          Recipe
                        </button>
                        <button
                          type="button"
                          className="cursor-pointer rounded border border-slate-300 px-1.5 py-0.5 text-[10px] hover:bg-slate-50"
                          onClick={() => void loadSchema(e.endpoint_key, 'schema')}
                        >
                          Schema
                        </button>
                        <button
                          type="button"
                          className="cursor-pointer rounded border border-slate-300 px-1.5 py-0.5 text-[10px] hover:bg-slate-50"
                          onClick={() => void loadSchema(e.endpoint_key, 'field_catalog')}
                        >
                          Fields
                        </button>
                        <button
                          type="button"
                          className="cursor-pointer rounded border border-slate-300 px-1.5 py-0.5 text-[10px] hover:bg-slate-50"
                          onClick={() =>
                            void amazonConnectApi
                              .downloadDiscoveryEndpointZip(connectionId, e.endpoint_key)
                              .catch((err) => setError(String(err)))
                          }
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          disabled={running}
                          className="cursor-pointer rounded border border-slate-300 px-1.5 py-0.5 text-[10px] hover:bg-slate-50 disabled:opacity-50"
                          onClick={() => void runAgain(e.endpoint_key)}
                        >
                          Run
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data && tab === 'recipes' && (
        <section className="space-y-4">
          <p className="text-xs text-slate-500">
            Postman workspace is the execution recipe reference. Docs:{' '}
            <code className="text-[11px]">docs/amazon/discovery/SP_API_EXECUTION_RECIPES.md</code>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm">
              <span className="text-slate-500">API</span>
              <select
                className="ml-2 rounded-md border border-slate-300 px-2 py-1 font-mono text-xs"
                value={selectedKey}
                onChange={(e) => void loadSchema(e.target.value, 'recipes')}
              >
                <option value="">Select…</option>
                {(data.recipes ?? []).map((r) => (
                  <option key={r.endpoint_key} value={r.endpoint_key}>
                    {r.endpoint_key}
                  </option>
                ))}
              </select>
            </label>
            {selectedKey && (
              <>
                <button
                  type="button"
                  className="cursor-pointer rounded border border-slate-300 px-2 py-1 text-xs"
                  onClick={() => void loadSchema(selectedKey, 'schema')}
                >
                  Schema
                </button>
                <button
                  type="button"
                  className="cursor-pointer rounded border border-slate-300 px-2 py-1 text-xs"
                  onClick={() => void loadSchema(selectedKey, 'field_catalog')}
                >
                  Field Catalog
                </button>
                <button
                  type="button"
                  className="cursor-pointer rounded border border-slate-300 px-2 py-1 text-xs"
                  onClick={() =>
                    void amazonConnectApi
                      .downloadDiscoveryEndpointZip(connectionId, selectedKey)
                      .catch((err) => setError(String(err)))
                  }
                >
                  Download ZIP
                </button>
              </>
            )}
          </div>
          {pkgLoading && <p className="text-sm text-slate-500">Loading recipe…</p>}
          {(pkg?.recipe || recipeByKey.get(selectedKey)) && (
            <RecipePanel recipe={(pkg?.recipe || recipeByKey.get(selectedKey))!} how={pkg?.how_to_unblock} />
          )}
        </section>
      )}

      {tab === 'schema' && (
        <section className="space-y-3">
          <EndpointPicker data={data} selectedKey={selectedKey} onPick={(k) => void loadSchema(k, 'schema')} />
          {pkgLoading && <p className="text-sm text-slate-500">Loading package…</p>}
          {pkg && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <h3 className="text-xs font-semibold uppercase text-slate-500">Business meaning</h3>
                <p className="text-sm">
                  {pkg.business_meaning ||
                    String((pkg.definition as { purpose?: string })?.purpose ?? '')}
                </p>
                <p className="font-mono text-xs">Auth: {pkg.authentication}</p>
                <p className="font-mono text-xs">
                  Status:{' '}
                  <span className={statusClass(pkg.row?.last_status || 'UNKNOWN')}>
                    {pkg.row?.last_status || 'UNKNOWN'}
                  </span>
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <h3 className="text-xs font-semibold uppercase text-slate-500">Dependencies</h3>
                <p className="mt-1 text-[11px] font-mono">{pkg.dependencies?.join(' → ') || '—'}</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <h3 className="text-xs font-semibold uppercase text-slate-500">Request schema</h3>
                <pre className="mt-1 max-h-48 overflow-auto text-[10px] font-mono">
                  {JSON.stringify(pkg.request_schema ?? {}, null, 2)}
                </pre>
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <h3 className="text-xs font-semibold uppercase text-slate-500">Response schema</h3>
                <pre className="mt-1 max-h-48 overflow-auto text-[10px] font-mono">
                  {JSON.stringify(pkg.response_schema ?? {}, null, 2)}
                </pre>
              </div>
              <div className="rounded-lg border border-slate-200 p-3 lg:col-span-2">
                <h3 className="text-xs font-semibold uppercase text-slate-500">Sample response (raw)</h3>
                <pre className="mt-1 max-h-48 overflow-auto text-[10px] font-mono">
                  {(pkg.sample?.body_raw || '(no sample — Run Lab)').slice(0, 8000)}
                </pre>
              </div>
              <div className="flex flex-wrap gap-2 lg:col-span-2">
                <DownloadBtn
                  label="Download JSON sample"
                  onClick={() =>
                    downloadText(`${pkg.endpoint_key}-sample.json`, pkg.sample?.body_raw || '{}')
                  }
                />
                <DownloadBtn
                  label="Download Schema"
                  onClick={() =>
                    downloadText(
                      `${pkg.endpoint_key}-schema.json`,
                      JSON.stringify(
                        { request: pkg.request_schema, response: pkg.response_schema },
                        null,
                        2,
                      ),
                    )
                  }
                />
                <DownloadBtn
                  label="Download Field Catalog ZIP"
                  onClick={() =>
                    void amazonConnectApi
                      .downloadDiscoveryEndpointZip(connectionId, pkg.endpoint_key)
                      .catch((err) => setError(String(err)))
                  }
                />
              </div>
            </div>
          )}
        </section>
      )}

      {tab === 'field_catalog' && (
        <section className="space-y-3">
          <EndpointPicker
            data={data}
            selectedKey={selectedKey}
            onPick={(k) => void loadSchema(k, 'field_catalog')}
          />
          {pkgLoading && <p className="text-sm text-slate-500">Loading field catalog…</p>}
          {pkg && (
            <>
              <p className="text-xs text-slate-600">{pkg.business_meaning}</p>
              <p className="text-[11px] text-slate-500">
                Execution recipe: {pkg.recipe?.steps?.length ?? 0} steps · Field mapping{' '}
                {pkg.field_intel?.length ?? 0} rows
              </p>
              <div className="max-h-[36rem] overflow-auto rounded-lg border border-slate-200">
                <table className="min-w-full text-left text-[11px]">
                  <thead className="sticky top-0 bg-slate-100">
                    <tr>
                      <th className="px-2 py-2">Field</th>
                      <th className="px-2 py-2">Path</th>
                      <th className="px-2 py-2">Type</th>
                      <th className="px-2 py-2">Meaning</th>
                      <th className="px-2 py-2">Warehouse candidate</th>
                      <th className="px-2 py-2">Consumers</th>
                      <th className="px-2 py-2">Conf</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pkg.field_intel ?? []).map((f) => (
                      <tr key={f.json_path} className="border-t border-slate-100 align-top">
                        <td className="px-2 py-1 font-mono">{f.field_name}</td>
                        <td className="max-w-[12rem] truncate px-2 py-1 font-mono text-[10px]">
                          {f.json_path}
                        </td>
                        <td className="px-2 py-1">{f.data_type}</td>
                        <td className="max-w-xs px-2 py-1">{f.business_meaning}</td>
                        <td className="px-2 py-1 font-mono text-[10px]">{f.warehouse_candidate}</td>
                        <td className="max-w-[10rem] px-2 py-1 text-[10px]">
                          {f.future_consumers?.join(', ')}
                        </td>
                        <td className="px-2 py-1">{f.confidence}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <DownloadBtn
                label="Download Field Catalog JSON"
                onClick={() =>
                  downloadText(
                    `${pkg.endpoint_key}-field-catalog.json`,
                    JSON.stringify(pkg.field_intel ?? [], null, 2),
                  )
                }
              />
            </>
          )}
        </section>
      )}

      {tab === 'capabilities' && (
        <section className="space-y-4">
          <p className="text-xs text-slate-500">
            docs/amazon/discovery/BUSINESS_CAPABILITY_MATRIX.md · warehouse not implemented
          </p>
          {(intel?.capabilities ?? []).map((c) => (
            <article key={c.name} className="rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">{c.name}</h3>
              <div className="mt-2 grid gap-3 text-xs sm:grid-cols-3">
                <div>
                  <h4 className="font-medium text-slate-500">Uses</h4>
                  <ul className="mt-1 list-inside list-disc font-mono text-[11px]">
                    {(c.uses_apis_reports ?? []).map((u) => (
                      <li key={u}>{u}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-slate-500">Produces</h4>
                  <ul className="mt-1 list-inside list-disc">
                    {(c.produces ?? []).map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-slate-500">Future consumers</h4>
                  <ul className="mt-1 list-inside list-disc">
                    {(c.future_consumers ?? []).map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[11px] text-slate-500">WH: {c.warehouse_hint}</p>
                </div>
              </div>
            </article>
          ))}
          {!intel?.capabilities?.length && (
            <p className="text-sm text-slate-500">Load connection to view capability matrix.</p>
          )}
        </section>
      )}

      {tab === 'lineage' && (
        <section className="space-y-4">
          <p className="text-xs text-slate-500">
            Amazon → Raw → Canonical → WH → API → FE → Reports → Analytics → AI
          </p>
          {(pkg?.field_lineages?.length ? pkg.field_lineages : (intel?.lineages ?? [])).map((L) => (
            <article key={L.field} className="rounded-lg border border-slate-200 p-3">
              <h3 className="font-mono text-sm font-semibold">{L.field}</h3>
              <p className="text-[11px] text-slate-500">{L.source_amazon}</p>
              <ol className="mt-2 space-y-0.5 text-[11px] font-mono">
                {(L.steps ?? []).map((s, i) => (
                  <li key={i}>
                    <span className="text-slate-500">{s.stage}:</span> {s.name}
                    {s.note ? <span className="text-slate-400"> — {s.note}</span> : null}
                  </li>
                ))}
              </ol>
            </article>
          ))}
        </section>
      )}

      {tab === 'warehouse' && (
        <section className="space-y-3">
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
            Documentation only. WAREHOUSE-FOUNDATION-000001 is NOT AUTHORIZED. No SQL / no
            implementation.
          </p>
          {(pkg?.warehouse_candidates?.length
            ? pkg.warehouse_candidates
            : (intel?.warehouse_blueprint ?? [])
          ).map((w) => (
            <article key={w.entity} className="rounded-lg border border-slate-200 p-3 text-xs">
              <h3 className="text-sm font-semibold">{w.entity}</h3>
              <dl className="mt-2 grid gap-1 sm:grid-cols-2">
                <div>
                  <dt className="text-slate-500">Fact</dt>
                  <dd className="font-mono">{w.fact_table_candidate}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">PK</dt>
                  <dd>{w.primary_key_candidate}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Dimensions</dt>
                  <dd className="font-mono text-[11px]">{w.dimension_table_candidates?.join(', ')}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Refresh / retention</dt>
                  <dd>
                    {w.refresh_frequency} · {w.retention}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-slate-500">Sources</dt>
                  <dd className="font-mono text-[11px]">{w.source_endpoints?.join(', ')}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-slate-500">Future metrics</dt>
                  <dd>{w.future_business_metrics?.join(', ')}</dd>
                </div>
              </dl>
            </article>
          ))}
        </section>
      )}

      {tab === 'consumers' && (
        <section className="space-y-3">
          <EndpointPicker
            data={data}
            selectedKey={selectedKey}
            onPick={(k) => void loadSchema(k, 'consumers')}
          />
          {pkg && (
            <div className="rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold">{pkg.endpoint_key}</h3>
              <ul className="mt-2 list-inside list-disc text-sm">
                {(pkg.consumers?.length ? pkg.consumers : ['Discovery Lab']).map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
              {(pkg.capabilities ?? []).length > 0 && (
                <p className="mt-3 text-xs text-slate-500">
                  Capabilities: {pkg.capabilities!.map((c) => c.name).join(', ')}
                </p>
              )}
            </div>
          )}
          {!pkg && <p className="text-sm text-slate-500">Select an API to list future consumers.</p>}
        </section>
      )}

      {tab === 'downloads' && (
        <section className="space-y-3">
          <p className="text-sm text-slate-600">Founder downloads — knowledge exports only.</p>
          <div className="flex flex-col gap-2 sm:max-w-md">
            <DownloadBtn
              label="Complete Discovery Documentation ZIP"
              onClick={() =>
                void amazonConnectApi.downloadDiscoveryAllZip(connectionId).catch((e) => setError(String(e)))
              }
            />
            <DownloadBtn
              label="Business Capability Matrix + Lineage + Warehouse Blueprint (+ FIELD_CATALOG)"
              onClick={() =>
                void amazonConnectApi
                  .downloadDiscoveryIntelligenceZip(connectionId)
                  .catch((e) => setError(String(e)))
              }
            />
            <DownloadBtn
              label="Amazon_API_Field_Matrix.csv — per-API fields + sample values"
              onClick={() =>
                void amazonConnectApi
                  .downloadDiscoveryFieldMatrixCSV(connectionId)
                  .catch((e) => setError(String(e)))
              }
            />
            <DownloadBtn
              label="Amazon_Field_Master.csv — normalized master field dictionary"
              onClick={() =>
                void amazonConnectApi
                  .downloadDiscoveryFieldMasterCSV(connectionId)
                  .catch((e) => setError(String(e)))
              }
            />
            {selectedKey ? (
              <DownloadBtn
                label={`Single API Field Catalog ZIP — ${selectedKey}`}
                onClick={() =>
                  void amazonConnectApi
                    .downloadDiscoveryEndpointZip(connectionId, selectedKey)
                    .catch((e) => setError(String(e)))
                }
              />
            ) : (
              <p className="text-xs text-slate-500">
                Select an API on Schema/Field Catalog tab for single-API ZIP.
              </p>
            )}
          </div>
        </section>
      )}

      {data && tab === 'reports' && (
        <section>
          <div className="max-h-96 overflow-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-left text-xs">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="px-2 py-2">ReportType</th>
                  <th className="px-2 py-2">Name</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">WH hint</th>
                </tr>
              </thead>
              <tbody>
                {data.reports.map((r) => (
                  <tr key={r.report_type} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 font-mono text-[11px]">{r.report_type}</td>
                    <td className="px-2 py-1.5">{r.official_name}</td>
                    <td className="px-2 py-1.5">
                      <span className={`rounded px-1.5 py-0.5 ${statusClass(r.last_status)}`}>
                        {r.last_status}
                      </span>
                    </td>
                    <td className="max-w-xs truncate px-2 py-1.5 text-[11px]">{r.warehouse_recommendation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data && tab === 'fields' && (
        <section>
          <div className="max-h-96 overflow-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-left text-xs">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="px-2 py-2">Field</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2">Sample</th>
                  <th className="px-2 py-2">Appears in</th>
                </tr>
              </thead>
              <tbody>
                {data.fields.slice(0, 200).map((f) => (
                  <tr key={f.field_path} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-mono text-[11px]">{f.field_path}</td>
                    <td className="px-2 py-1">{f.data_type}</td>
                    <td className="max-w-[10rem] truncate px-2 py-1">{f.sample_value}</td>
                    <td className="px-2 py-1 text-[11px]">{f.appears_in?.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data && tab === 'graph' && (
        <section className="grid gap-4 lg:grid-cols-2">
          <div>
            <h2 className="mb-2 text-sm font-semibold">Relationships</h2>
            <ul className="max-h-64 space-y-1 overflow-auto rounded-lg border border-slate-200 p-3 text-xs">
              {data.relationships.length === 0 && <li className="text-slate-500">Run lab to detect keys.</li>}
              {data.relationships.map((r) => (
                <li key={r.key_name}>
                  <span className="font-mono font-medium">{r.key_name}</span> · {r.endpoints?.join(' → ')}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="mb-2 text-sm font-semibold">Execution edges</h2>
            <ul className="max-h-64 space-y-1 overflow-auto rounded-lg border border-slate-200 p-3 font-mono text-[11px]">
              {(data.execution_graph?.edges ?? []).length === 0 && (
                <li className="font-sans text-slate-500">No edges yet.</li>
              )}
              {(data.execution_graph?.edges ?? []).map((e, i) => (
                <li key={i}>
                  {e[0]} → {e[1]}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}

function EndpointPicker({
  data,
  selectedKey,
  onPick,
}: {
  data: DiscoveryExplorerPayload | null;
  selectedKey: string;
  onPick: (k: string) => void;
}) {
  return (
    <label className="text-sm">
      <span className="text-slate-500">Endpoint</span>
      <select
        className="ml-2 rounded-md border border-slate-300 px-2 py-1 font-mono text-xs"
        value={selectedKey}
        onChange={(e) => onPick(e.target.value)}
      >
        <option value="">Select API…</option>
        {(data?.endpoints ?? []).map((e) => (
          <option key={e.endpoint_key} value={e.endpoint_key}>
            {e.endpoint_key}
          </option>
        ))}
      </select>
    </label>
  );
}

function DownloadBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm hover:bg-slate-50"
    >
      {label}
    </button>
  );
}

function downloadText(filename: string, body: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([body], { type: 'application/json' }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function RecipePanel({ recipe, how }: { recipe: DiscoveryRecipe; how?: string }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-lg border border-slate-200 p-3 text-sm">
        <h3 className="font-mono text-xs font-semibold text-slate-900">{recipe.endpoint_key}</h3>
        <p className="mt-1 text-xs text-slate-600">{recipe.purpose}</p>
        <p className="mt-2 font-mono text-[11px]">
          {recipe.http_method} {recipe.path}
        </p>
        <p className="mt-1 text-[11px]">Auth: {recipe.authentication}</p>
        <p className="mt-1 text-[11px]">Depends: {recipe.depends_on?.join(' → ') || 'none'}</p>
        {(how || recipe.how_to_unblock) && (
          <p className="mt-2 rounded bg-amber-50 p-2 text-[11px] text-amber-950">
            <span className="font-semibold">How to unblock:</span> {how || recipe.how_to_unblock}
          </p>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 p-3">
        <h4 className="text-xs font-semibold uppercase text-slate-500">Execution order</h4>
        <ol className="mt-2 list-decimal space-y-1 pl-4 text-xs">
          {(recipe.steps ?? []).map((s) => (
            <li key={s.order}>
              {s.action}
              {s.api ? ` (${s.api})` : ''}
              {s.extract ? ` → ${s.extract}` : ''}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
